/**
 * Curated country list for the shared `PhoneField` (DNKADM-0084) — DATA ONLY.
 *
 * The business serves the US/Canada + Mexico + Central America + Colombia, plus a
 * couple of common extras (UK, Spain). This is deliberately a SHORT, curated set,
 * NOT an "all countries" list — adding a country later is one entry here. No phone
 * library backs it (the spec's lean-deps decision); the picker reads this module
 * and the small helpers in `./phoneFormat`.
 *
 * Each entry carries the ISO code, a display name, the dial code (digits only, no
 * `+`), a flag emoji, and a light national-number FORMAT HINT — a grouping mask
 * where `X` is a digit slot and every other character is a literal separator used
 * for DISPLAY ONLY (e.g. US `(XXX) XXX-XXXX`). The emitted value is always E.164
 * (`+<dialCode><digits>`); the mask never affects what is stored.
 *
 * NANP: the US and Canada SHARE `+1`. On parse a `+1` number defaults to the US
 * (it appears first below) — the admin can switch the flag to Canada; the E.164 is
 * identical either way. We do not infer country from the area code.
 */

/** One curated country option. `dialCode` is digits only (no leading `+`). */
export interface Country {
  /** ISO 3166-1 alpha-2 code (the stable option key). */
  iso: string;
  /** Display name shown in the searchable list. */
  name: string;
  /** Dial code, digits only (no `+`) — e.g. `'1'`, `'506'`. */
  dialCode: string;
  /** Flag emoji shown beside the dial code. */
  flag: string;
  /**
   * National-number grouping mask for DISPLAY ONLY: `X` = a digit slot, any other
   * character is a literal separator. Storage is always plain E.164 digits.
   */
  format: string;
}

/**
 * The curated set. US first so a shared-`+1` (NANP) number defaults to the US on
 * parse. Order here is also the list/search order in the picker.
 */
export const COUNTRIES: Country[] = [
  { iso: 'US', name: 'United States', dialCode: '1', flag: '🇺🇸', format: '(XXX) XXX-XXXX' },
  { iso: 'CA', name: 'Canada', dialCode: '1', flag: '🇨🇦', format: '(XXX) XXX-XXXX' },
  { iso: 'MX', name: 'Mexico', dialCode: '52', flag: '🇲🇽', format: 'XX XXXX XXXX' },
  { iso: 'GT', name: 'Guatemala', dialCode: '502', flag: '🇬🇹', format: 'XXXX XXXX' },
  { iso: 'BZ', name: 'Belize', dialCode: '501', flag: '🇧🇿', format: 'XXX-XXXX' },
  { iso: 'SV', name: 'El Salvador', dialCode: '503', flag: '🇸🇻', format: 'XXXX XXXX' },
  { iso: 'HN', name: 'Honduras', dialCode: '504', flag: '🇭🇳', format: 'XXXX-XXXX' },
  { iso: 'NI', name: 'Nicaragua', dialCode: '505', flag: '🇳🇮', format: 'XXXX XXXX' },
  { iso: 'CR', name: 'Costa Rica', dialCode: '506', flag: '🇨🇷', format: 'XXXX XXXX' },
  { iso: 'PA', name: 'Panama', dialCode: '507', flag: '🇵🇦', format: 'XXXX-XXXX' },
  { iso: 'CO', name: 'Colombia', dialCode: '57', flag: '🇨🇴', format: 'XXX XXX XXXX' },
  { iso: 'GB', name: 'United Kingdom', dialCode: '44', flag: '🇬🇧', format: 'XXXX XXXXXX' },
  { iso: 'ES', name: 'Spain', dialCode: '34', flag: '🇪🇸', format: 'XXX XX XX XX' },
];

/** The fallback country when the locale is undetectable (NANP / US). */
const DEFAULT_COUNTRY_ISO = 'US';

/** Find a curated country by ISO code, or `undefined`. */
export function countryByIso(iso: string): Country | undefined {
  const upper = iso.toUpperCase();
  return COUNTRIES.find((c) => c.iso === upper);
}

/** The default country object — always present (US is curated). */
export function defaultCountry(): Country {
  // US is always in the list; the non-null assertion is safe and keeps the return
  // type non-optional for callers.
  return countryByIso(DEFAULT_COUNTRY_ISO) ?? COUNTRIES[0]!;
}
